import { ModalConstructor } from "../ModalConstructor";
import { Stack } from "@mui/material";
import { usePostListModalStore } from "./PostListModalStore";
import React, { useEffect, useRef, useState } from "react";
import { PostListModalHeader } from "./PostListModalHeader/FeedTeaser/PostListModalHeader";
import { useGlobalConfigs } from "@infrastructure/Hooks/useGlobalConfigs";
import { usePostCardClickHandler } from "../../../Pages/GroupDetailsPage/Hooks/usePostCardClickHandler";
import { usePostFormModalStore } from "../../../Pages/GroupDetailsPage/Stores/PostFormModalStore";
import { useUserStore } from "@infrastructure/Persistence/UserState";
import { SORT_CRITERIA_LIST } from "@shared/consts";
import { groupPostsFetcher } from "../../../DataFetchers/GroupPostsFetcher";
import {
    POST_SOURCE_TYPE,
    POST_FORM_CONTENT_INPUT,
} from "../../Post/PostForm/PostFormModal.types";
import {
    POST_LIST_TYPES,
    PostCreationResponse,
    PostType,
} from "../../Post/Post.types";
import { updatePostFromServer } from "../../../DataFetchers/PostDataFetcher";
import {
    PostsListType,
    PostWrapperOptsType,
} from "../../DashboardComponents/PostList/PostsList.types";
import { useInitialUserLocationStore } from "../../../Pages/GroupDetailsPage/Stores/InitialUserLocationStore";
import { HeadlineTeaserBoxWithPostFormButtons } from "../../Group/HeadlineTeaserBox/HeadlineTeaserBox";
import { Box } from "@mui/system";
import PostsList from "../../DashboardComponents/PostList/PostsList";
import { PostFormModal } from "../../Post/PostForm/PostFormModal";
import { EntryNewPostButton } from "../../NewPostButton/EntryNewPostButton";
import { usePostDetailsModalStore } from "../PostDetailsModal/PostDetailsModalStore";
import {
    GroupInput,
    WRAPPER_ACTIONS_ENUM,
} from "@shared/Types/GroupEntry.types";
import { PostDetailsPageActions } from "../PostDetailsModal/PostDetailsModal";
import { PostUpdateResponse } from "@infrastructure/Services/GroupService.types";
import { getLocalSelectedPostSortOption } from "@infrastructure/Persistence/LocalUserStorage";
import { SortItemType } from "../../SortingChips/SortingChips.types";
import { POST_TYPES } from "@infrastructure/Services/PostService.types";
import { useGroupSelector } from "../../GroupSelector/useGroupSelector";
import { FeedActionButtons } from "../../../Pages/GroupDetailsPage/Components/FeedActionButtons/FeedActionButtons";
import { ActiveGamesIndicator } from "../../ActiveGamesIndicator/ActiveGamesIndicator";

export function PostListModal({ groupData }: { groupData: GroupInput }) {
    const {
        isPostListModalOpen,
        setIsPostListModalOpen,
        postListAction,
        setPostListAction,
        selectedGroupId,
    } = usePostListModalStore();

    const { setIsPostDetailsModalOpen, setPostDetailsAction } =
        usePostDetailsModalStore();

    const { getConfigs } = useGlobalConfigs();
    const tippspielFeedEnabled = getConfigs()?.tippspielFeedEnabled;
    const onPostCardClick = usePostCardClickHandler(false);

    const handleClose = () => {
        setIsPostListModalOpen(false);
        setIsPostFormModalOpen(false);
        setPostListAction(undefined);
        setIsPostDetailsModalOpen(false);
    };

    const { userState } = useUserStore();
    const initialSelectedSortingCriteria = getLocalSelectedPostSortOption();

    const defaultSortCriteria =
        SORT_CRITERIA_LIST[
            Math.min(
                initialSelectedSortingCriteria,
                SORT_CRITERIA_LIST.length - 1,
            )
        ];
    const [selectedSortCriteria, setSelectedSortCriteria] =
        useState<SortItemType>(defaultSortCriteria);

    const { preparedFiltersForPost, mainGroupId: groupId } = useGroupSelector();

    useEffect(() => {
        if (!groupId) {
            return;
        }

        const newPostsListOpt = { ...postsListOpt };
        newPostsListOpt.fetchParams.groupId = parseInt(groupId);
        setPostsListOpt(newPostsListOpt);
    }, [userState, groupId]);

    const [postsListOpt, setPostsListOpt] = useState({
        fetcher: groupPostsFetcher,
        fetchParams: {
            groupId: selectedGroupId,
            sortCriteria: {
                ...selectedSortCriteria.criterion,
                filterActiveGames: true,
                isHighlighted: 0,
                ...preparedFiltersForPost,
            },
        },
        preparedParams: {
            boxed: true,
            showPreviewComment: true,
            onCardClickHandler: onPostCardClick,
            showCommentButton: true,
            wrapperMarginBottomValue: "16px",
        },
        maxItemCount: 5000,
    });

    useEffect(() => {
        const newPostsListOpt = { ...postsListOpt };
        if (preparedFiltersForPost) {
            newPostsListOpt.fetchParams.sortCriteria = {
                ...newPostsListOpt.fetchParams.sortCriteria,
                ...selectedSortCriteria.criterion,
                ...preparedFiltersForPost,
            };
        } else {
            newPostsListOpt.fetchParams.sortCriteria = {
                ...newPostsListOpt.fetchParams.sortCriteria,
                ...selectedSortCriteria.criterion,
            };
            delete newPostsListOpt.fetchParams.sortCriteria.selectedGroups;
        }

        newPostsListOpt.fetchParams.sortCriteria = {
            ...newPostsListOpt.fetchParams.sortCriteria,
        };

        setPostsListOpt(newPostsListOpt);
    }, [selectedSortCriteria, preparedFiltersForPost]);

    const { selectedPost, setSelectedPost, setIsPostFormModalOpen } =
        usePostFormModalStore();

    function openPostFormHandler(sourceType?: POST_SOURCE_TYPE) {
        setIsPostFormModalOpen(
            true,
            POST_FORM_CONTENT_INPUT.DEFAULT,
            sourceType,
        );
    }

    const onPostEdit = (post: PostType) => {
        setIsPostFormModalOpen(
            true,
            post.postData.type === POST_TYPES.POLL
                ? POST_FORM_CONTENT_INPUT.POLL_INPUT
                : POST_FORM_CONTENT_INPUT.DEFAULT,
        );
        setSelectedPost(post);
    };

    const updatePostRef = useRef(
        (id: string, stringPostId: string, post: PostType) => {},
    );

    const onPostEditedSuccessfully = (
        post: PostType,
        response: PostCreationResponse | PostUpdateResponse,
    ) => {
        if (selectedPost?.id) {
            onPostCardClick?.(post.id.toString());
        } else if (response.id) {
            onPostCardClick?.(response.id);
        }
    };

    const updateGroupPostFromServer = (postID: number) => {
        updatePostFromServer(postID, updatePostRef.current);
    };

    const postFormModalCloseCallback = () => {
        setSelectedSortCriteria(defaultSortCriteria);
        setSelectedPost(undefined);
    };

    const [postsAreLoading, setPostsAreLoading] = useState(true);

    const postWrapperOpts: PostWrapperOptsType = {
        isInfiniteScrollEnabled: true,
        showShareOption: false,
        onPostEdit: onPostEdit,
        openPostFormHandler: openPostFormHandler,
        updatePostFromServer: updateGroupPostFromServer,
    };

    const postsListOpts: PostsListType = {
        scrollLoaderOpts: postsListOpt,
        postWrapperOpts: postWrapperOpts,
        listType: POST_LIST_TYPES.DASHBOARD_NEWEST_POSTS,
        autoLoadDataAfterClick: false,
        loadingMoreButtonText: "Mehr anzeigen",
        setPostsAreLoading: setPostsAreLoading,
    };

    useEffect(() => {
        if (
            postListAction?.action === WRAPPER_ACTIONS_ENUM.OPEN_POST_DETAILS &&
            postListAction?.postId
        ) {
            setIsPostDetailsModalOpen(true, parseInt(postListAction.postId));
        } else if (
            postListAction?.action === WRAPPER_ACTIONS_ENUM.OPEN_POST_COMMENT &&
            postListAction?.postId
        ) {
            setPostDetailsAction({
                desiredAction: PostDetailsPageActions.PIN_COMMENT,
                commentId: postListAction?.commentId ?? 0,
            });
            setIsPostDetailsModalOpen(true, parseInt(postListAction.postId));
        }

        return () => {
            setPostDetailsAction(undefined);
            setIsPostFormModalOpen(false);
        };
    }, [postListAction]);

    const { initialUserLocation, setInitialUserLocation } =
        useInitialUserLocationStore();

    const [showCreatePostFabButton, setShowCreatePostFabButton] =
        useState(false);

    const scrollableContainerRef = useRef<HTMLDivElement>();
    const startOfPostsListRef = useRef();

    if (!tippspielFeedEnabled) {
        return null;
    }

    const tippspielFeedCreatePostEnabled =
        getConfigs()?.tippspielFeedCreatePostEnabled;

    return (
        <>
            <ModalConstructor
                onClose={handleClose}
                open={isPostListModalOpen}
                scrollableContainerRef={scrollableContainerRef}
                modalContent={
                    <>
                        <Stack
                            sx={{
                                padding: "12px",
                                backgroundColor: "#efeff4",
                                overflowX: "hidden",
                            }}
                        >
                            <ActiveGamesIndicator />
                            {tippspielFeedCreatePostEnabled && (
                                <HeadlineTeaserBoxWithPostFormButtons
                                    onComponentVisible={() => {
                                        setShowCreatePostFabButton(false);
                                    }}
                                    onComponentOutOfSight={() => {
                                        const isWindowBelowStartOfPostsList: boolean =
                                            startOfPostsListRef.current?.getBoundingClientRect()
                                                .bottom <= window.innerHeight;
                                        setShowCreatePostFabButton(
                                            isWindowBelowStartOfPostsList,
                                        );
                                    }}
                                />
                            )}
                            <Box ref={startOfPostsListRef} />
                            <FeedActionButtons
                                isLoading={postsAreLoading}
                                onUpdate={setSelectedSortCriteria}
                            />
                            <PostsList
                                ref={updatePostRef}
                                {...postsListOpts}
                                onPostCardClick={onPostCardClick}
                                showLoadMoreButton={true}
                                scrollableContainerRef={scrollableContainerRef}
                                isInsideModal={true}
                            />
                            {showCreatePostFabButton &&
                                tippspielFeedCreatePostEnabled && (
                                    <EntryNewPostButton
                                        onClick={() => {
                                            setIsPostFormModalOpen(
                                                true,
                                                POST_FORM_CONTENT_INPUT.DEFAULT,
                                                POST_SOURCE_TYPE.CREATE_POST_FAB,
                                            );
                                        }}
                                    />
                                )}
                        </Stack>
                    </>
                }
                modalTitle={<PostListModalHeader groupData={groupData} />}
                isInsideClosingButtonVisible={true}
                isBorderRadiusVisible={true}
                showModalActionsDivider={false}
                modalInnerContainerStyles={{
                    overflowY: "unset",
                    height: "calc(100vh - 24px)",
                    maxWidth: "640px",
                    "@media (min-width: 640px)": {
                        maxWidth: "640px",
                        borderRadius: "12px",
                    },
                    "& .MuiDialogContent-root": {
                        padding: "0",
                        borderRadius: "0",
                    },
                    "& .MuiDialogActions-root": {
                        justifyContent: "flex-start",
                        "& > :not(:first-of-type)": {
                            marginLeft: 0,
                        },
                    },
                    "& .MuiDialogTitle-root": {
                        margin: 0,
                        padding: "12px",
                        borderBottom:
                            "1px solid var(--community-color-dark-gray)",
                    },
                }}
            />

            <PostFormModal
                postFormModalCloseCallback={postFormModalCloseCallback}
                data={selectedPost}
                postFormSubmissionCallback={onPostEditedSuccessfully}
                initialUserLocation={initialUserLocation}
                setInitialUserLocation={setInitialUserLocation}
            />
        </>
    );
}
